import { useState, useMemo } from 'react';
import { useCollection } from '../../../hooks/useCollection';
import { sendTelegramNotification } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';
import { requestToRecord, logAudit, fullName } from '../utils/blacklistService';

const isToday = (d) => d && new Date(d).toDateString() === new Date().toDateString();
const riskColor = (l) => ({ critical: '#7f1d1d', high: '#dc2626', medium: '#f59e0b', low: '#64748b' }[String(l || '').toLowerCase()] || '#64748b');

/**
 * Super-Admin Blacklist Approval Center: review agency submissions, approve
 * (→ add to global blacklist), reject, or request more information.
 */
export default function BlacklistApprovalCenter({ T = {}, dm = false, saActor = 'super-admin' }) {
  const { data: requests, update: updateRequest } = useCollection('blacklist_requests', { bypassAgencyFilter: true });
  const { data: globalList, add: addToGlobal } = useCollection('blacklist', { bypassAgencyFilter: true });

  const [filter, setFilter] = useState('pending');
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const filtered = useMemo(() =>
    requests.filter(r => filter === 'all' || (r.status || 'pending') === filter)
      .sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0)),
    [requests, filter]);

  const stats = useMemo(() => ({
    totalGlobal: globalList.length,
    pending: requests.filter(r => (r.status || 'pending') === 'pending').length,
    approvedToday: requests.filter(r => r.status === 'approved' && isToday(r.approvedAt)).length,
    rejectedToday: requests.filter(r => r.status === 'rejected' && isToday(r.rejectedAt)).length,
  }), [requests, globalList]);

  const byAgency = useMemo(() => {
    const m = {};
    requests.forEach(r => { const a = r.agencyName || r.agency_id || '—'; m[a] = (m[a] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [requests]);

  const notifyAgency = (req, template, extra) => {
    if (!req.agencyChatId) return;
    try {
      const msg = buildTelegramMessage(template, { name: fullName(req), time: new Date().toLocaleString('fr-FR'), ...extra });
      sendTelegramNotification(msg, template, req.agencyChatId);
    } catch (_) {}
  };

  const approve = async (req) => {
    if (!confirm(`Approuver et ajouter ${fullName(req)} à la liste noire globale ?`)) return;
    setBusy(true);
    try {
      const record = requestToRecord(req, saActor);
      const ins = await addToGlobal(record);
      await updateRequest(req.id, { status: 'approved', approvedBy: saActor, approvedAt: new Date().toISOString(), globalId: ins?.id || '' });
      await logAudit('approval', { actor: saActor, agencyId: req.agency_id, targetId: ins?.id, targetName: fullName(req), details: { requestId: req.id } });
      notifyAgency(req, 'blacklist_approved', { approver: saActor });
      notify('✅ Approuvé et ajouté à la liste noire');
      setDetail(null);
    } catch (e) { notify('❌ ' + e.message); }
    setBusy(false);
  };

  const reject = async (req) => {
    const reason = prompt('Motif du rejet :', '');
    if (reason === null) return;
    setBusy(true);
    try {
      await updateRequest(req.id, { status: 'rejected', rejectedBy: saActor, rejectedAt: new Date().toISOString(), rejectReason: reason });
      await logAudit('rejection', { actor: saActor, agencyId: req.agency_id, targetName: fullName(req), details: { requestId: req.id, reason } });
      notifyAgency(req, 'blacklist_rejected', { reason });
      notify('❌ Rejeté');
      setDetail(null);
    } catch (e) { notify('❌ ' + e.message); }
    setBusy(false);
  };

  const requestInfo = async (req) => {
    const message = prompt('Informations demandées à l\'agence :', '');
    if (message === null) return;
    setBusy(true);
    try {
      await updateRequest(req.id, { status: 'more_info', moreInfoMessage: message, moreInfoAt: new Date().toISOString() });
      await logAudit('edited', { actor: saActor, agencyId: req.agency_id, targetName: fullName(req), details: { requestId: req.id, moreInfo: message } });
      notifyAgency(req, 'blacklist_moreinfo', { message });
      notify('ℹ️ Demande d\'informations envoyée');
      setDetail(null);
    } catch (e) { notify('❌ ' + e.message); }
    setBusy(false);
  };

  const statusBadge = (st) => {
    const map = { pending: ['#fef3c7', '#b45309', '⏳ En attente'], approved: ['#dcfce7', '#15803d', '✅ Approuvé'], rejected: ['#fee2e2', '#b91c1c', '❌ Rejeté'], more_info: ['#dbeafe', '#1d4ed8', 'ℹ️ Infos requises'] };
    const [bg, col, lbl] = map[st] || map.pending;
    return <span style={{ background: bg, color: col, padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 800 }}>{lbl}</span>;
  };

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 900, color: T.text, margin: 0 }}>🛡️ Centre d'approbation — Liste noire</h2>
        <div style={{ fontSize: '13px', color: T.textMuted }}>Validez les signalements des agences avant leur ajout à la liste noire globale.</div>
      </div>

      {/* Widgets */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {[
          ['Total liste noire', stats.totalGlobal, '#6366f1'],
          ['En attente', stats.pending, '#f59e0b'],
          ['Approuvés aujourd\'hui', stats.approvedToday, '#10b981'],
          ['Rejetés aujourd\'hui', stats.rejectedToday, '#ef4444'],
        ].map(([label, val, color]) => (
          <div key={label} style={{ flex: '1 1 150px', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '14px', padding: '14px 18px' }}>
            <div style={{ fontSize: '26px', fontWeight: 900, color }}>{val}</div>
            <div style={{ fontSize: '12px', color: T.textSub, fontWeight: 600 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {[['pending', 'En attente'], ['more_info', 'Infos requises'], ['approved', 'Approuvés'], ['rejected', 'Rejetés'], ['all', 'Tous']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} style={{ padding: '6px 12px', borderRadius: '8px', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer', background: filter === k ? '#6366f1' : (dm ? '#1e293b' : '#e2e8f0'), color: filter === k ? '#fff' : T.textSub }}>{l}</button>
        ))}
      </div>

      {/* Requests table */}
      <div style={{ overflowX: 'auto', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '12px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead><tr style={{ background: dm ? '#1e293b' : '#f8fafc', color: T.textSub, textAlign: 'left' }}>{['Agence', 'Personne', 'Motif', 'Montant', 'Date', 'Statut', ''].map(h => <th key={h} style={{ padding: '9px', fontWeight: 800 }}>{h}</th>)}</tr></thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={7} style={{ padding: '16px', textAlign: 'center', color: T.textSub }}>Aucune demande.</td></tr>
              : filtered.map(r => (
                <tr key={r.id} style={{ borderTop: `1px solid ${T.cardBorder}`, color: T.text }}>
                  <td style={{ padding: '9px', fontWeight: 700 }}>{r.agencyName || r.agency_id || '—'}</td>
                  <td style={{ padding: '9px' }}>{fullName(r)}</td>
                  <td style={{ padding: '9px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason || '—'}</td>
                  <td style={{ padding: '9px' }}>{r.outstanding || r.damageAmount ? `${r.outstanding || r.damageAmount} ${r.currency || 'DH'}` : '—'}</td>
                  <td style={{ padding: '9px', whiteSpace: 'nowrap' }}>{r.submittedAt ? new Date(r.submittedAt).toLocaleDateString('fr-FR') : '—'}</td>
                  <td style={{ padding: '9px' }}>{statusBadge(r.status || 'pending')}</td>
                  <td style={{ padding: '9px' }}><button onClick={() => setDetail(r)} style={{ padding: '4px 10px', borderRadius: '7px', background: '#6366f1', color: '#fff', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>Voir</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Requests by agency */}
      {byAgency.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 800, color: T.text, margin: '0 0 6px' }}>Demandes par agence</h3>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {byAgency.map(([a, n]) => <span key={a} style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '999px', padding: '4px 12px', fontSize: '12px', color: T.text }}>{a}: <b>{n}</b></span>)}
          </div>
        </div>
      )}

      {/* Detail / action modal */}
      {detail && (
        <div onClick={e => e.target === e.currentTarget && setDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px' }}>
          <div style={{ background: T.card || '#fff', borderRadius: '16px', width: '100%', maxWidth: '620px', maxHeight: '92vh', overflowY: 'auto', padding: '22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0, fontWeight: 900, color: T.text }}>👤 {fullName(detail)}</h3>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: T.textSub }}>×</button>
            </div>
            <div style={{ marginBottom: '10px' }}>{statusBadge(detail.status || 'pending')} <span style={{ marginLeft: '8px', background: riskColor(detail.riskLevel), color: '#fff', padding: '2px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 800 }}>{detail.riskLevel || 'Medium'}</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 16px', fontSize: '13px', color: T.text }}>
              <div><b>Agence :</b> {detail.agencyName || detail.agency_id}</div>
              <div><b>Soumis par :</b> {detail.submittedBy || '—'}</div>
              <div><b>C.N.I.E :</b> {detail.cnie || '—'}</div>
              <div><b>Permis :</b> {detail.license || '—'}</div>
              <div><b>Passeport :</b> {detail.passport || '—'}</div>
              <div><b>Naissance :</b> {detail.birthDate || '—'}</div>
              <div><b>Téléphone :</b> {detail.phone || '—'}</div>
              <div><b>Ville :</b> {detail.city || '—'}</div>
              <div style={{ gridColumn: '1 / -1' }}><b>Adresse :</b> {detail.address || '—'}</div>
              <div><b>Véhicule :</b> {detail.vehicleReg || '—'}</div>
              <div><b>N° contrat :</b> {detail.contractNumber || '—'}</div>
              <div><b>Dégâts :</b> {detail.damageAmount ? `${detail.damageAmount} ${detail.currency || 'DH'}` : '—'}</div>
              <div><b>Impayé :</b> {detail.outstanding ? `${detail.outstanding} ${detail.currency || 'DH'}` : '—'}</div>
              <div style={{ gridColumn: '1 / -1' }}><b>Motif :</b> {detail.reason || '—'}</div>
              <div style={{ gridColumn: '1 / -1' }}><b>Description :</b> {detail.incident || '—'}</div>
            </div>
            {Array.isArray(detail.attachments) && detail.attachments.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: T.text }}>
                <b>📎 Documents :</b> {detail.attachments.map((a, i) => <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', marginRight: '8px' }}>{a.name || `Doc ${i + 1}`}</a>)}
              </div>
            )}
            {detail.rejectReason && <div style={{ marginTop: '8px', fontSize: '13px', color: '#dc2626' }}><b>Motif rejet :</b> {detail.rejectReason}</div>}
            {detail.moreInfoMessage && <div style={{ marginTop: '8px', fontSize: '13px', color: '#2563eb' }}><b>Infos demandées :</b> {detail.moreInfoMessage}</div>}

            {(detail.status || 'pending') !== 'approved' && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
                <button onClick={() => approve(detail)} disabled={busy} style={{ flex: '1 1 130px', padding: '11px', borderRadius: '10px', border: 'none', background: '#16a34a', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>✅ Approuver</button>
                <button onClick={() => reject(detail)} disabled={busy} style={{ flex: '1 1 130px', padding: '11px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>❌ Rejeter</button>
                <button onClick={() => requestInfo(detail)} disabled={busy} style={{ flex: '1 1 130px', padding: '11px', borderRadius: '10px', border: 'none', background: '#3b82f6', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>ℹ️ Plus d'infos</button>
              </div>
            )}
          </div>
        </div>
      )}

      {toast && <div style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 100001, background: '#111827', color: '#fff', padding: '10px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: 700 }}>{toast}</div>}
    </div>
  );
}
